import { useEffect } from 'react'

interface SEOProps {
    title?: string
    description?: string
    keywords?: string
    canonical?: string
    ogType?: 'website' | 'article'
    ogImage?: string
    publishedTime?: string
    author?: string
}

const DEFAULT_TITLE = 'Rapheal Kwabena Asomani Gadoh | Full Stack Engineer & STEM Innovator'
const DEFAULT_DESCRIPTION = 'Full Stack Engineer, Open Source Creator & STEM Educator. Building scalable web platforms, automation tools, and Python libraries.'
const DEFAULT_KEYWORDS = 'Rapheal Kwabena, Rapheal Gadoh, Rapheal Kwabena Asomani Gadoh, raphealgadoh, raphealgadoh.me, Full Stack Engineer, Software Engineer, Python, React, TypeScript, Automation, STEM Education, Open Source, Ghana'
const DEFAULT_CANONICAL = 'https://www.raphealgadoh.me/'
const DEFAULT_OG_IMAGE = 'https://www.raphealgadoh.me/og-image.png'

export default function SEO({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    keywords = DEFAULT_KEYWORDS,
    canonical = DEFAULT_CANONICAL,
    ogType = 'website',
    ogImage = DEFAULT_OG_IMAGE,
    publishedTime,
    author = 'Rapheal Kwabena Asomani Gadoh'
}: SEOProps) {
    useEffect(() => {
        // Document Title
        document.title = title

        // Helper to update or create meta tag
        const updateMeta = (selector: string, attribute: 'name' | 'property', attrValue: string, content: string) => {
            let element = document.querySelector(selector)
            if (!element) {
                element = document.createElement('meta')
                element.setAttribute(attribute, attrValue)
                document.head.appendChild(element)
            }
            element.setAttribute('content', content)
        }

        // Helper for canonical link
        const updateLink = (rel: string, href: string) => {
            let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
            if (!element) {
                element = document.createElement('link')
                element.setAttribute('rel', rel)
                document.head.appendChild(element)
            }
            element.setAttribute('href', href)
        }

        // Standard Meta Tags
        updateMeta('meta[name="description"]', 'name', 'description', description)
        updateMeta('meta[name="keywords"]', 'name', 'keywords', keywords)
        updateMeta('meta[name="author"]', 'name', 'author', author)

        // Open Graph Tags
        updateMeta('meta[property="og:title"]', 'property', 'og:title', title)
        updateMeta('meta[property="og:description"]', 'property', 'og:description', description)
        updateMeta('meta[property="og:type"]', 'property', 'og:type', ogType)
        updateMeta('meta[property="og:url"]', 'property', 'og:url', canonical)
        updateMeta('meta[property="og:image"]', 'property', 'og:image', ogImage)

        // Twitter Card Tags
        updateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title)
        updateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description)
        updateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage)

        // Article specific
        if (ogType === 'article' && publishedTime) {
            updateMeta('meta[property="article:published_time"]', 'property', 'article:published_time', publishedTime)
            updateMeta('meta[property="article:author"]', 'property', 'article:author', author)
        }

        // Canonical Tag
        updateLink('canonical', canonical)
    }, [title, description, keywords, canonical, ogType, ogImage, publishedTime, author])

    return null
}
